/**
 * UGC Gallery Toggle (overkicks / custom "cc-" gallery).
 *
 * This theme's PDP gallery is a self-contained custom block (NOT standard Dawn
 * <media-gallery>, NOT a slider library):
 *
 *   .cc-gallery-container (position:relative)
 *     #ScuffersGallery.cc-gallery-grid   -> desktop 2-col CSS grid / mobile flex scroll-snap
 *       .cc-media-item  -> <img> or <video>
 *     #CounterPill.cc-counter-pill -> mobile counter: <span id="cc-current"> / <span id="cc-total">
 *
 * Swapping to UGC = replacing the .cc-media-item children of #ScuffersGallery
 * with the UGC set (kept in a <template data-ugc-slides>), updating the counter
 * total, resetting scroll, and re-binding the page's existing lightbox to the
 * new images. There is no Flickity / is-active state machine and variant changes
 * do not touch #ScuffersGallery, so no variant guard is needed.
 */
(function () {
  'use strict';

  class UgcGalleryToggle {
    constructor(root) {
      this.root = root; // <product-info>
      this.container = root.querySelector('.cc-gallery-container');
      this.grid = root.querySelector('#ScuffersGallery');
      this.tabs = root.querySelector('[data-gallery-tabs]');
      this.slidesTpl = root.querySelector('template[data-ugc-slides]');
      this.totalEl = root.querySelector('#cc-total');
      this.currentEl = root.querySelector('#cc-current');

      if (!this.grid || !this.tabs || !this.slidesTpl) return;

      this.mode = 'media'; // 'media' | 'ugc'
      this.ugcMounted = false;

      // Detached holders for whichever set is not currently in the grid.
      this.mediaHolder = document.createElement('div');
      this.ugcHolder = document.createElement('div');

      this.bindTabs();
    }

    bindTabs() {
      this.tabs.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-tab-target]');
        if (!btn) return;
        const target = btn.dataset.tabTarget;
        if (target === this.mode) return; // active tab is hidden anyway
        this.switchTo(target);
      });
    }

    setActiveTab(mode) {
      this.tabs.querySelectorAll('[data-tab-target]').forEach((b) => {
        const isActive = b.dataset.tabTarget === mode; // active = current view; CSS hides it
        b.classList.toggle('is-active', isActive);
        b.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });
    }

    switchTo(mode) {
      if (mode === this.mode) return;

      if (mode === 'ugc') this.mountUgc();
      else this.mountMedia();

      this.mode = mode;
      this.setActiveTab(mode);

      this.updateCounter();
      this.resetScroll();
      this.bindLightbox(); // re-bind zoom to the now-visible images
    }

    mountUgc() {
      // Move current (media) items out, bring UGC in.
      this.moveChildren(this.grid, this.mediaHolder);
      if (!this.ugcMounted) {
        this.grid.appendChild(this.slidesTpl.content.cloneNode(true));
        this.ugcMounted = true;
      } else {
        this.moveChildren(this.ugcHolder, this.grid);
      }
    }

    mountMedia() {
      // Move UGC out (keep for next toggle), bring media back.
      this.moveChildren(this.grid, this.ugcHolder);
      this.moveChildren(this.mediaHolder, this.grid);
    }

    updateCounter() {
      const count = this.grid.querySelectorAll('.cc-media-item').length;
      if (this.totalEl) this.totalEl.textContent = count;
      if (this.currentEl) this.currentEl.textContent = '1';
    }

    resetScroll() {
      // Mobile gallery is a horizontal scroll-snap row; reset to the first item.
      this.grid.scrollTo({ left: 0, behavior: 'auto' });
    }

    /* The page builds a single #scuffers-lightbox on DOMContentLoaded and binds
       zoom to the .cc-media-item img that existed at load time — i.e. the PRODUCT
       images only. Swapped-in UGC images miss that binding, so we bind the same
       zoom behavior to the UGC images ([data-ugc]) ourselves, reusing the existing
       lightbox element. We deliberately bind ONLY UGC items: product images stay
       owned by the page's native handler, so nothing gets a duplicate click
       listener when toggling back and forth. */
    bindLightbox() {
      const lightbox = document.getElementById('scuffers-lightbox');
      if (!lightbox) return;
      const lightboxImg = lightbox.querySelector('img');
      if (!lightboxImg) return;

      this.grid.querySelectorAll('.cc-media-item[data-ugc] img').forEach((img) => {
        if (img.dataset.ugcLightboxBound) return;
        img.dataset.ugcLightboxBound = 'true';
        img.style.cursor = 'zoom-in';
        img.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          lightboxImg.src = img.src;
          lightbox.style.display = 'flex';
          document.body.style.overflow = 'hidden';
          requestAnimationFrame(() => {
            lightbox.style.opacity = '1';
          });
        });
      });
    }

    moveChildren(from, to) {
      while (from.firstChild) to.appendChild(from.firstChild);
    }
  }

  function init() {
    document.querySelectorAll('product-info').forEach((root) => {
      if (root.dataset.ugcToggleBound) return;
      if (!root.querySelector('[data-gallery-tabs]')) return; // no UGC for this product
      root.dataset.ugcToggleBound = 'true';
      new UgcGalleryToggle(root);
    });
  }

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);
})();
